import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);


// ================================================================================
// parallax animations
// ================================================================================
export function nnGsapParallax() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // default values for parallax animations
    const PARALLAX_FROM_Y = '-100px';
    const PARALLAX_TO_Y = '100px';

    const $parallaxTargets = document.querySelectorAll('[data-nn-parallax]');

    $parallaxTargets.forEach(($target) => {
        const fromYVars = () => parallaxYVars($target, '--parallax-f-y-percent', '--parallax-f-y', PARALLAX_FROM_Y);
        const toYVars = () => parallaxYVars($target, '--parallax-t-y-percent', '--parallax-t-y', PARALLAX_TO_Y);
        const startPad = () => viewportPad(getCssVar($target, '--parallax-start', '0px'));
        const endPad = () => viewportPad(getCssVar($target, '--parallax-end', '0px'));

        gsap.fromTo($target, {
            y: () => fromYVars().y,
            yPercent: () => fromYVars().yPercent,
        }, {
            y: () => toYVars().y,
            yPercent: () => toYVars().yPercent,
            ease: 'none',
            immediateRender: true,
            scrollTrigger: {
                trigger: $target,
                start: () => `top bottom+=${startPad()}`,
                end: () => `bottom top-=${endPad()}`,
                scrub: true,
                invalidateOnRefresh: true,
            },
        });
    });
}



// ================================================================================
// progress
// ================================================================================
export function nnGsapVpProgress($target, options = {}) {
    if (!$target) return;

    const opts = {
        targetRefPosition: '0%',
        endFromTop: '0%',
        startFromBottom: '0%',
        fromToRange: [0.00, 100.00],
        ...options,
    };

    const [from, to] = opts.fromToRange;
    const cssVar = '--vp-progress';

    const setProgress = (progress) => {
        const value = from + (to - from) * progress;
        $target.style.setProperty(cssVar, value.toFixed(2));
    };

    const triggerPos = () => toScrollPos(opts.targetRefPosition);
    const endFromTop = () => viewportPad(toScrollPos(opts.endFromTop));
    const startFromBottom = () => viewportPad(toScrollPos(opts.startFromBottom));

    return ScrollTrigger.create({
        trigger: $target,
        start: () => `top bottom-=${startFromBottom()}`,
        end: () => `${triggerPos()} ${endFromTop()}`,
        invalidateOnRefresh: true,
        onUpdate: (self) => setProgress(self.progress),
        onRefresh: (self) => setProgress(self.progress),
    });
}



// ================================================================================
// utility functions
// ================================================================================

// ========== parallax ==========
// utility function to get a CSS variable from an element
function getCssVar($el, name, fallback) {
    return getComputedStyle($el).getPropertyValue(name).trim() || fallback;
}

function parseYPercent(raw) {
    if (!raw) return null;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : null;
}

function parallaxYVars($el, percentName, yName, yFallback) {
    const yPercent = parseYPercent(getComputedStyle($el).getPropertyValue(percentName).trim());
    if (yPercent != null) {
        return { y: 0, yPercent };
    }
    return { y: getCssVar($el, yName, yFallback), yPercent: 0 };
}


// ScrollTrigger only treats % as viewport-relative in start/end; 50vh would parse as 50px.
function viewportPad(value) {
    const v = String(value).trim();
    const m = v.match(/^(-?[\d.]+)((?:d|s|l)?vh|%)$/i);
    return m ? `${m[1]}%` : v;
}


// ========== progress ==========
function toScrollPos(value, fallback = '0%') {
    if (value == null || value === '') return fallback;
    if (typeof value === 'number' && Number.isFinite(value)) return `${value}%`;
    const v = String(value).trim();
    if (/^-?[\d.]+$/.test(v)) return `${v}%`;
    return v;
}